/**
 * Tickets collection — typed CRUD on top of the generic store.
 *
 * Wraps `db` so views deal in `Ticket` records and so we can centralise
 * id minting (sequential `TIQ-###`), timestamp stamping, and timeline
 * appending. Modelled on `accounts.ts` — the account `audit` trail is
 * the same pattern as our `timeline`. When we cut over to Firestore the
 * only thing that changes is the adapter selection in `store.ts`.
 */

import { db, nowIso, uuid } from './store';
import type {
  Ticket,
  TicketComment,
  TicketEvent,
  TicketPriority,
  TicketStatus,
} from './types';
import { seedTickets } from './seed';

/** Roster of valid owners — humans plus Claude agents. */
export const TICKET_OWNERS = [
  'Trent',
  'Grant',
  'claude:worker',
  'claude:fixer',
  'claude:researcher',
] as const;

const event = (
  kind: TicketEvent['kind'],
  actor: string,
  text: string,
  at = nowIso(),
): TicketEvent => ({ id: uuid(), at, actor, kind, text });

export async function listTickets(): Promise<Ticket[]> {
  return db.list('tickets');
}

export async function getTicket(id: string): Promise<Ticket | null> {
  return db.get('tickets', id);
}

export function subscribeTickets(fn: (records: Ticket[]) => void): () => void {
  return db.subscribe('tickets', fn);
}

/** Mint the next sequential id ("TIQ-001", "TIQ-002", …). */
async function nextTicketId(): Promise<string> {
  const all = await db.list('tickets');
  let max = 0;
  for (const t of all) {
    const m = /^TIQ-(\d+)$/.exec(t.id);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return `TIQ-${String(max + 1).padStart(3, '0')}`;
}

export interface NewTicketInput {
  title: string;
  spec?: string;
  priority?: TicketPriority;
  owner?: string | null;
  createdBy?: string;
  estimateHours?: number | null;
  deadline?: string | null;
  labels?: string[];
}

export async function createTicket(input: NewTicketInput): Promise<Ticket> {
  const ts = nowIso();
  const createdBy = input.createdBy ?? 'You';
  const id = await nextTicketId();
  const record: Ticket = {
    id,
    title: input.title,
    spec: input.spec ?? '',
    status: 'open',
    priority: input.priority ?? 'p2',
    owner: input.owner ?? null,
    createdBy,
    estimateHours: input.estimateHours ?? null,
    deadline: input.deadline ?? null,
    startedAt: null,
    labels: input.labels ?? [],
    timeline: [event('created', createdBy, `Created ${id}`, ts)],
    comments: [],
    createdAt: ts,
    updatedAt: ts,
  };
  await db.add('tickets', record);
  return record;
}

/**
 * Patch a ticket. Re-stamps `updatedAt`, appends a timeline event, and
 * stamps `startedAt` the first time the status moves to `in-progress`.
 */
export async function updateTicket(
  id: string,
  patch: Partial<Omit<Ticket, 'id' | 'createdAt' | 'timeline' | 'comments'>>,
  actor = 'You',
): Promise<Ticket> {
  const existing = await db.get('tickets', id);
  if (!existing) throw new Error(`No ticket ${id}`);
  const ts = nowIso();

  const events: TicketEvent[] = [];
  if (patch.status && patch.status !== existing.status) {
    events.push(
      event('status', actor, `Status: ${existing.status} → ${patch.status}`, ts),
    );
  }
  if (patch.owner !== undefined && patch.owner !== existing.owner) {
    events.push(
      event('assigned', actor, `Owner: ${existing.owner ?? '—'} → ${patch.owner ?? '—'}`, ts),
    );
  }
  // Catch-all for other field edits so nothing changes silently.
  const otherKeys = Object.keys(patch).filter(
    (k) => k !== 'status' && k !== 'owner' && k !== 'updatedAt',
  );
  if (otherKeys.length > 0) {
    events.push(event('updated', actor, `Updated ${otherKeys.join(', ')}`, ts));
  }

  const startedAt =
    patch.status === 'in-progress' && !existing.startedAt
      ? ts
      : existing.startedAt;

  return db.update('tickets', id, {
    ...patch,
    startedAt,
    timeline: [...existing.timeline, ...events],
    updatedAt: ts,
  });
}

/** Set status with a timeline entry. */
export async function setTicketStatus(
  id: string,
  status: TicketStatus,
  actor = 'You',
): Promise<Ticket> {
  return updateTicket(id, { status }, actor);
}

/**
 * Claim a ticket — set the owner and move it to `claimed` (or
 * `in-progress` if the caller is already working it). Appends a
 * `claimed` timeline entry. This is what an agent "takes a ticket"
 * calls.
 */
export async function claimTicket(
  id: string,
  owner: string,
  actor = owner,
): Promise<Ticket> {
  const existing = await db.get('tickets', id);
  if (!existing) throw new Error(`No ticket ${id}`);
  const ts = nowIso();
  return db.update('tickets', id, {
    owner,
    status: existing.status === 'in-progress' ? 'in-progress' : 'claimed',
    timeline: [...existing.timeline, event('claimed', actor, `Claimed by ${owner}`, ts)],
    updatedAt: ts,
  });
}

/** Append a comment + a `comment` timeline entry. */
export async function addTicketComment(
  id: string,
  author: string,
  body: string,
): Promise<Ticket> {
  const existing = await db.get('tickets', id);
  if (!existing) throw new Error(`No ticket ${id}`);
  const ts = nowIso();
  const comment: TicketComment = { id: uuid(), at: ts, author, body };
  return db.update('tickets', id, {
    comments: [...existing.comments, comment],
    timeline: [...existing.timeline, event('comment', author, `Commented`, ts)],
    updatedAt: ts,
  });
}

export async function deleteTicket(id: string): Promise<void> {
  await db.remove('tickets', id);
}

/** Seed the tickets collection on first run. Idempotent. */
export async function bootstrapTicketsIfEmpty(): Promise<void> {
  const existing = await db.list('tickets');
  if (existing.length > 0) return;
  await db.replaceAll('tickets', seedTickets);
}

/** Reset the tickets collection back to seed data. Useful for dev. */
export async function resetTicketsToSeed(): Promise<void> {
  await db.replaceAll('tickets', seedTickets);
}
